import React, { useState, useEffect, useCallback, useRef } from "react"
import { Spinner, Alert } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import { Page, ModalView } from "@src/components"
import { SLIM_DEVICE_TYPE } from "@src/devices/types"
import { useLocalization } from "@src/localization"
import { usePartner } from "@src/partner"
import { useFleets } from "@src/settings"
import { DEVICE_LOADING_STATE, useDevice } from "../useDevice"
import { DevicesTable } from "./components/DevicesTable"
import { ExportButton } from "./components/ExportButton"
import { FilterBar } from "./components/FilterBar"
import { PaginationControls } from "./components/PaginationControls"
import { exportCsv } from "./csvExport"
import { useDeviceList } from "./useDeviceList"
import { useFilters } from "./useFilters"

export const DevicesScreen: React.FC = () => {
  const navigate = useNavigate()
  const { formatDateTime } = useLocalization()
  const { t } = useTranslation()
  const { partner, devicesLength, updateDeviceLength } = usePartner()
  const { partnerUuid: urlPartnerUuid } = useParams()
  const { fleet } = useFleets()
  const { loadDevice } = useDevice()

  const [activePage, setActivePage] = useState<number>(0)
  const [sorting, setSorting] = useState<boolean>(true)
  const [sortHeader, setSortedHeader] = useState<string>("last_seen_timestamp")
  const [showModal, setShowModal] = useState<boolean>(false)
  const [exporting, setExporting] = useState<boolean>(false)
  const exportCancelledRef = useRef<boolean>(false)

  const filters = useFilters()
  const effectiveUuid = partner?.uuid || urlPartnerUuid

  const deviceList = useDeviceList({
    activePage,
    sorting,
    sortHeader,
    devicesLength,
    filters: filters.buildFilters(),
    searchParams: filters.searchParams,
  })

  useEffect(() => {
    if (effectiveUuid) {
      setActivePage(0)
    }
  }, [effectiveUuid])

  const changePage = useCallback(
    (page: number) => {
      if ((page < 0 || deviceList.pagesCount < page) && !!partner) return
      deviceList.setLoading(activePage !== page)
      setActivePage(page)
    },
    [partner, deviceList.pagesCount, activePage, deviceList],
  )

  const onClickTableHeader = useCallback(
    (selectedHeader: string) => {
      if (selectedHeader !== sortHeader) {
        setSorting(true)
      } else {
        setSorting(!sorting)
      }
      if (selectedHeader !== "servicebooking") {
        setSortedHeader(selectedHeader)
        deviceList.setHeaderLoad(true)
      }
    },
    [sortHeader, sorting, deviceList],
  )

  const handleChangeDevicesLength = useCallback(
    (value: number) => {
      setActivePage(0)
      if (value > 50) {
        setShowModal(true)
      } else {
        updateDeviceLength(value)
      }
    },
    [updateDeviceLength],
  )

  const handleOnClick = useCallback(
    (item: SLIM_DEVICE_TYPE, event: MouseEvent) => {
      loadDevice(item.serialnumber, DEVICE_LOADING_STATE.load)
      const effectiveUuid = partner?.uuid || urlPartnerUuid
      if (event.metaKey || event.ctrlKey) {
        const win = window.open(`/${effectiveUuid}/device/${item.serialnumber}`, "_blank")
        win?.focus()
      } else {
        navigate(`/${effectiveUuid}/device/${item.serialnumber}`)
      }
    },
    [loadDevice, navigate, partner?.uuid, urlPartnerUuid],
  )

  const handleExportCsv = useCallback(async () => {
    if (!effectiveUuid) return
    exportCancelledRef.current = false
    setExporting(true)
    try {
      await exportCsv(effectiveUuid, fleet?.uuid, sorting, sortHeader, filters.buildFilters(), exportCancelledRef)
    } finally {
      setExporting(false)
      exportCancelledRef.current = false
    }
  }, [effectiveUuid, fleet, sorting, sortHeader, filters])

  const handleUpdateParam = useCallback(
    (key: string, value?: string | null) => {
      filters.updateParam(key, value)
      setActivePage(0)
    },
    [filters],
  )

  return (
    <Page
      showFleet
      showHeader
      headerValue={t("sidebar.devices")}
      onSelectFleet={() => {
        deviceList.setLoading(true)
      }}
    >
      <FilterBar
        filterReportedStolen={filters.filterReportedStolen}
        setFilterReportedStolen={filters.setFilterReportedStolen}
        filterLockState={filters.filterLockState}
        setFilterLockState={filters.setFilterLockState}
        filterCriticalBattery={filters.filterCriticalBattery}
        setFilterCriticalBattery={filters.setFilterCriticalBattery}
        filterServiceMode={filters.filterServiceMode}
        setFilterServiceMode={filters.setFilterServiceMode}
        hasActiveFilters={filters.hasActiveFilters}
        onResetFilters={() => {
          filters.resetFilters()
          setActivePage(0)
        }}
        onUpdateParam={handleUpdateParam}
        searchParams={filters.searchParams}
        setSearchParams={filters.setSearchParams}
        setActivePage={setActivePage}
      />

      <PaginationControls
        activePage={activePage}
        pagesCount={deviceList.pagesCount}
        onChangePage={changePage}
        devicesLength={devicesLength}
        onChangeDevicesLength={handleChangeDevicesLength}
        rightContent={<ExportButton exporting={exporting} onClick={handleExportCsv} />}
      />

      {deviceList.loading && (
        <div className="d-flex justify-content-center mt-4 mb-4">
          <Spinner animation="border" />
        </div>
      )}
      {deviceList.devices.length == 0 && !deviceList.loading && (
        <Alert variant="secondary">{t("devices_screen.no_device")}</Alert>
      )}
      {deviceList.devices.length > 0 && !deviceList.loading && (
        <DevicesTable
          devices={deviceList.devices}
          sorting={sorting}
          sortHeader={sortHeader}
          headerLoad={deviceList.headerLoad}
          onClickTableHeader={onClickTableHeader}
          onRowClick={handleOnClick}
          formatDateTime={formatDateTime}
        />
      )}

      <ModalView
        title={t("settings.fleets.alert")}
        buttonTitle={t("device_screen.ok")}
        show={showModal}
        buttonLoader={false}
        onHideModal={() => setShowModal(false)}
        onButtonClick={() => {
          setShowModal(false)
          updateDeviceLength(100)
        }}
      >
        <label>{t("device_screen.pagination_msg")}</label>
      </ModalView>

      <ModalView
        title={t("devices_screen.export_title")}
        disableButton
        show={exporting}
        onHideModal={() => {
          exportCancelledRef.current = true
          setExporting(false)
        }}
        onButtonClick={() => {}}
        noFooter
        buttonTitle=""
        buttonLoader={false}
      >
        <div className="d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>
            {t("devices_screen.export_running") ||
              "Export wird vorbereitet. Dies kann etwas dauern. Bitte verlasse die Seite nicht."}
          </span>
        </div>
      </ModalView>
    </Page>
  )
}
